import { useTheme } from "@emotion/react";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  DataSource,
  Operator,
  ProviderType,
} from "@ternary/api-lib/analytics/enums";
import { Filter } from "@ternary/api-lib/analytics/ui/types";
import { roundDate } from "@ternary/api-lib/analytics/utils/DateUtils";
import {
  BillingRuleType,
  ReallocationType,
  RuleMeta,
} from "@ternary/api-lib/core/types/BillingRule";
import { TenantType } from "@ternary/api-lib/core/types/Tenant";
import { actions } from "@ternary/api-lib/telemetry";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import EmptyPlaceholder from "@ternary/api-lib/ui-lib/components/EmptyPlaceholder";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import { isEqual, keyBy, uniq } from "lodash";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import useGetDimensionValuesByDataSource from "../../../api/analytics/useGetDimensionValuesByDataSource";
import { UpdateBillingRuleParams } from "../../../api/core/types";
import paths from "../../../constants/paths";
import { useActivityTracker } from "../../../context/ActivityTrackerProvider";
import useAuthenticatedUser from "../../../hooks/useAuthenticatedUser";
import { useAvailableDimensionsByDataSourceV2 } from "../../../hooks/useAvailableDimensionsByDataSource";
import useGatekeeper from "../../../hooks/useGatekeeper";
import { DateHelper } from "../../../lib/dates";
import { useNavigateWithSearchParams } from "../../../lib/react-router";
import { useMspStore } from "../../../lib/zustand";
import { AlertType, postAlert } from "../../../utils/alerts";
import useGetDimensionPreferencesByTenantID from "../../admin/hooks/useGetDimensionPreferencesByTenantID";
import { useGetTenantsByParentTenantID } from "../../global-admin/hooks/useGetTenantsByParentTenantID";
import copyText from "../copyText";
import queryKeys from "../hooks/queryKeys";
import { useCreateBillingRule } from "../hooks/useCreateBillingRule";
import { useGetBillingRulesByTenantID } from "../hooks/useGetBillingRulesByTenantID";
import { useGetSubAccountIDsByTenantID } from "../hooks/useGetSubAccountIDsByTenantID";
import { useUpdateBillingRule } from "../hooks/useUpdateBillingRule";
import { BillingRuleForm } from "./BillingRuleForm/BillingRuleForm";
import { SubmitValues } from "./BillingRuleForm/types";

const defaultTenants = [];

type State = {
  dimensionNames: string[];
};

export function BillingRuleFormContainer() {
  const activityTracker = useActivityTracker();
  const authenticatedUser = useAuthenticatedUser();
  const gatekeeper = useGatekeeper();
  const navigate = useNavigateWithSearchParams();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const dateHelper = new DateHelper();

  //
  // URL State
  //

  const { ruleID = "" } = useParams();

  const isMspAdminLocation = location.pathname.startsWith(paths._mspMgmt);

  const isCreateMode = location.pathname.includes("new");

  //
  // State
  //

  const mspStore = useMspStore();

  const parentTenantID = mspStore.selectedParentTenantID as string;

  const tenantID = isMspAdminLocation
    ? parentTenantID
    : authenticatedUser.tenant.id;

  const [state, setState] = useState<State>({ dimensionNames: [] });

  //
  // Queries
  //

  const { data: tenants = defaultTenants, isLoading: isLoadingTenants } =
    useGetTenantsByParentTenantID(parentTenantID, {
      enabled: gatekeeper.canReadTenantsPartner && isMspAdminLocation,
      includeParentTenant: isMspAdminLocation,
    });

  const { data: billingRules = [], isLoading: isLoadingBillingRules } =
    useGetBillingRulesByTenantID(tenantID);

  const {
    data: dimensionPreferences = [],
    isLoading: isLoadingDimensionPreferences,
  } = useGetDimensionPreferencesByTenantID(tenantID);

  const availableDimensions = useAvailableDimensionsByDataSourceV2(
    DataSource.FOCUS_BILLING
  );

  const {
    data: dimensionValuesMap = {},
    isFetching: isLoadingDimensionValues,
  } = useGetDimensionValuesByDataSource(
    {
      dataSource: DataSource.FOCUS_BILLING,
      dateRange: [
        roundDate(dateHelper.nMonthsAgo(24)),
        roundDate(dateHelper.date),
      ],
      dimensions: state.dimensionNames.map((dimensionName) => ({
        displayName: dimensionName,
        providerType: ProviderType.FOCUS,
        schemaName: dimensionName,
      })),
      parentTenantID: isMspAdminLocation ? parentTenantID : undefined,
    },
    { enabled: state.dimensionNames.length > 0 }
  );

  const { data: subAccountIDs = [], isLoading: isLoadingSubAccountIDs } =
    useGetSubAccountIDsByTenantID({
      dateRange: [
        roundDate(dateHelper.nMonthsAgo(24)),
        roundDate(dateHelper.date),
      ],
    });

  //
  // Mutations
  //

  const { isPending: isCreatingRule, mutate: createRule } =
    useCreateBillingRule({
      onError: () => {
        postAlert({
          message: copyText.errorCreatingBillingRuleMessage,
          type: AlertType.ERROR,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.billingRules(tenantID),
        });

        postAlert({
          message: copyText.successCreatingBillingRuleMessage,
          type: AlertType.SUCCESS,
        });

        const path = isMspAdminLocation
          ? paths._mspBillingRulesEngine
          : paths._costAssistReallocations;

        navigate(path);
      },
    });

  const { isPending: isUpdatingRule, mutate: updateRule } =
    useUpdateBillingRule({
      onError: () => {
        postAlert({
          message: copyText.errorUpdatingBillingRuleMessage,
          type: AlertType.ERROR,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.billingRules(tenantID),
        });

        postAlert({
          message: copyText.successUpdatingBillingRuleMessage,
          type: AlertType.SUCCESS,
        });

        const path = isMspAdminLocation
          ? paths._mspBillingRulesEngine
          : paths._costAssistReallocations;

        navigate(path);
      },
    });

  const billingRulesKeyedByID = keyBy(billingRules, "id");

  //
  // Side Effects
  //

  useEffect(() => {
    if (!ruleID) return;

    const selectedRule = billingRulesKeyedByID[ruleID];

    if (!selectedRule) return;

    const dimensionNames = selectedRule.meta.filters
      ? [...selectedRule.meta.filters.map((filter) => filter.schemaName)]
      : [];

    setState({ dimensionNames });
  }, [ruleID]);

  //
  // Interaction Handlers
  //

  function handleCreateBillingRule(values: SubmitValues) {
    let priority: number | null = null;

    if (values.setRulePriority) {
      const newRule = values.minimalRules.find((rule) => rule.id === "new");

      if (newRule) {
        priority = newRule.priority;
      }
    }

    const mspTenantParams = isMspAdminLocation
      ? {
          applyToAllTenants: values.applyToAllTenants,
          applyToTenantIDs:
            values.applyToAllTenants === true
              ? []
              : values.applyToTenants.map((tenant) => tenant.id),
        }
      : {};

    const params = {
      tenantID,
      ...(values.endDate ? { endTime: values.endDate.toISOString() } : {}),
      name: values.name,
      ...(priority ? { priority: priority } : {}),
      startTime: values.startDate.toISOString(),
      type: values.ruleType,
      meta: getRuleMeta(values),
      ...mspTenantParams,
    };

    createRule(params);
  }

  function handleUpdateBillingRule(values: SubmitValues) {
    const selectedRule = billingRulesKeyedByID[ruleID];

    if (!selectedRule) return;

    // Create base params with only changed values
    const params: UpdateBillingRuleParams = {};

    // Only include changed fields
    if (values.name !== selectedRule.name) {
      params.name = values.name;
    }

    const newEndTime = values.endDate ? values.endDate.toISOString() : null;

    if (newEndTime !== selectedRule.endTime) {
      params.endTime = values.endDate?.toISOString();
    }

    if (values.startDate.toISOString() !== selectedRule.startTime) {
      params.startTime = values.startDate.toISOString();
    }

    if (values.applyToAllTenants !== selectedRule.applyToAllTenants) {
      params.applyToAllTenants = values.applyToAllTenants;
    }

    const tenantIDs =
      values.applyToAllTenants === true
        ? []
        : values.applyToTenants.map((tenant) => tenant.id);

    if (!isEqual(tenantIDs, selectedRule.applyToTenantIDs)) {
      params.applyToTenantIDs = tenantIDs;
    }

    const newMeta = getRuleMeta(values);

    // Compare meta objects and only include if changed
    if (!isEqual(newMeta, selectedRule.meta)) {
      params.meta = newMeta;
    }

    updateRule({ ruleID, type: selectedRule.type, ...params });
  }

  function handleInteraction(interaction: BillingRuleForm.Interaction) {
    switch (interaction.type) {
      case BillingRuleForm.INTERACTION_CANCEL_BUTTON_CLICKED: {
        const path = isMspAdminLocation
          ? paths._mspBillingRulesEngine
          : paths._costAssistReallocations;

        navigate(path);
        break;
      }
      case BillingRuleForm.INTERACTION_SUBMIT_BUTTON_CLICKED: {
        if (isCreateMode) {
          activityTracker.captureAction(actions.CLICK_BILLING_RULE_CREATE);

          handleCreateBillingRule(interaction.values);
        } else {
          activityTracker.captureAction(actions.CLICK_BILLING_RULE_UPDATE);

          handleUpdateBillingRule(interaction.values);
        }
        break;
      }
      case BillingRuleForm.INTERACTION_UPDATE_DIMENSIONS:
        setState((currentState) => ({
          dimensionNames: uniq([
            ...currentState.dimensionNames,
            interaction.dimensionName,
          ]),
        }));
        break;
    }
  }

  function handleClickBack() {
    const path = isMspAdminLocation
      ? paths._mspBillingRulesEngine
      : paths._costAssistReallocations;

    navigate(path);
  }

  //
  // Render
  //

  const isLoading =
    isLoadingTenants || isLoadingBillingRules || isLoadingDimensionPreferences;

  const billingRule = billingRulesKeyedByID[ruleID];

  return (
    <Flex direction="column" gap={theme.space_lg} maxWidth={800}>
      <Button
        iconStart={<Icon icon={faArrowLeft} />}
        secondary
        size="small"
        width="210px"
        onClick={handleClickBack}
      >
        {isMspAdminLocation
          ? copyText.backToBillingRulesEngineButtonLabel
          : copyText.backToCostAllocationsButtonLabel}
      </Button>
      <Text appearance="h3">
        {isCreateMode
          ? copyText.createRuleFormTitle
          : copyText.editRuleFormTitle}
      </Text>
      {isLoading ? (
        <EmptyPlaceholder loading={isLoading} />
      ) : (
        <BillingRuleForm
          availableDimensions={availableDimensions}
          billingRule={billingRule}
          billingRules={billingRules}
          dimensionPreferences={dimensionPreferences}
          dimensionValues={dimensionValuesMap}
          isLoadingBillingRules={isLoadingBillingRules}
          isLoadingDimensionPreferences={isLoadingDimensionPreferences}
          isLoadingDimensionValues={isLoadingDimensionValues}
          isLoadingSubAccountIDs={isLoadingSubAccountIDs}
          isMspAdmin={isMspAdminLocation}
          isProcessing={isCreatingRule || isUpdatingRule}
          subAccountIDs={subAccountIDs}
          tenants={tenants}
          tenantType={
            isMspAdminLocation
              ? TenantType.MSP_PARENT
              : authenticatedUser.tenant.type
          }
          onInteraction={handleInteraction}
        />
      )}
    </Flex>
  );
}

function convertTargetLabelsArrayToObject(
  labels: { key: string; value: string }[]
): Record<string, string> {
  return labels.reduce(
    (acc, label) => {
      acc[label.key] = label.value;
      return acc;
    },
    {} as Record<string, string>
  );
}

function removeIncompleteFilters(filters: Filter[]): Filter[] {
  return filters.filter((filter) => {
    const baseQualifiers =
      filter.displayName.length > 0 && filter.schemaName.length > 0;

    // If SET or NOT_SET values can be empty
    return filter.operator === Operator.SET ||
      filter.operator === Operator.NOT_SET
      ? baseQualifiers
      : baseQualifiers && filter.values && filter.values.length > 0;
  });
}

function getRuleMeta(values: SubmitValues): RuleMeta {
  switch (values.ruleType) {
    case BillingRuleType.ADJUST_TO_LIST: {
      const completeFilters = removeIncompleteFilters(values.filters);

      if (completeFilters.length === 0) {
        return {};
      }

      return { filters: completeFilters };
    }
    case BillingRuleType.APPLY_MARGIN: {
      const completeFilters = removeIncompleteFilters(values.filters);

      return {
        adjustToList: values.adjustToList,
        metrics: values.metrics,
        value: values.value,
        ...(completeFilters.length > 0 && { filters: completeFilters }),
      };
    }
    case BillingRuleType.BACKOUT_LINE_ITEM: {
      const completeFilters = removeIncompleteFilters(values.filters);

      return { filters: completeFilters };
    }
    case BillingRuleType.REALLOCATE_COSTS: {
      const config =
        values.reallocationType === ReallocationType.STATIC
          ? {
              type: ReallocationType.STATIC,
              targets: values.targetsStatic.map((target) => ({
                identifier: {
                  labels: convertTargetLabelsArrayToObject(
                    target.identifier.labels
                  ),
                  subAccountID: target.identifier.subAccountID,
                },
                share: target.share,
              })),
            }
          : {
              type: ReallocationType.DYNAMIC,
              dataSource: DataSource.FOCUS_BILLING,
              filters: values.filtersDynamic,
              measure: values.measure as string, // Measure should always be a string at this point.
              minimumShare: values.minimumShare,
              usageGroupingDimensions: values.usageGroupingDimensions,
            };

      return { filters: values.filters, reallocationConfig: config };
    }
    case BillingRuleType.RERATE: {
      const completeFilters = removeIncompleteFilters(values.filters);

      return {
        adjustToList: values.adjustToList,
        metrics: values.metrics,
        value: values.value,
        ...(completeFilters.length > 0 && { filters: completeFilters }),
      };
    }
    default: {
      return {};
    }
  }
}
